import { useState, useEffect } from 'react';
import { LoginPage } from './components/auth/LoginPage';
import { SuperAdminDashboard } from './components/dashboards/SuperAdminDashboard';
import { RestaurantAdminDashboard } from './components/dashboards/RestaurantAdminDashboard';
import { ManagerDashboard } from './components/dashboards/ManagerDashboard';
import { StaffDashboard } from './components/dashboards/StaffDashboard';
import { DeliveryPartnerDashboard } from './components/dashboards/DeliveryPartnerDashboard';
import { DeliveryPartnerApplicationPage } from './components/delivery/DeliveryPartnerApplicationPage';
import { CustomerView } from './components/customer/CustomerView';
import { LandingPage } from './components/landing/LandingPage';
// import { ErrorBoundary } from './components/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { api } from './lib/api';
import { getRestaurants, initializeMockData } from './lib/mockData';
import { initializeAnalyticsData } from './lib/generateAnalyticsData';

export type UserRole =
  | 'superadmin'
  | 'restaurant_admin'
  | 'manager'
  | 'staff'
  | 'delivery_partner'
  | 'customer';

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  restaurantId?: string;
  avatar?: string;
  phone?: string;
  address?: string;
  isActive?: boolean;
}

// Export User as UserType for backward compatibility
export type UserType = User;

export interface Restaurant {
  id: string;
  name: string;
  slug?: string;
  description: string;
  logo?: string;
  banner?: string;
  coverImage?: string; // For DineOut section
  integrationSetup?: any;
  integration_setup?: any;
  address: string;
  city: string;
  phone: string;
  email: string;
  latitude?: number;
  longitude?: number;
  primaryColor: string;
  secondaryColor: string;
  isActive: boolean;
  rating: number;
  totalOrders: number;
  totalRatings?: number; // For DineOut section
  cuisine: string[];
  openTime: string;
  closeTime: string;
  deliveryTime: string;
  minimumOrder: number;
  deliveryFee: number;
  freeDeliveryAbove: number;
  taxRate: number;
  packagingCharges: number;
  distance?: number; // in km
  isPureVeg?: boolean;
  dietaryTags?: string[]; // normalized dietary tag codes (e.g. 'veg', 'non-veg')
  foodTypeIds?: string[]; // master dietary tag IDs (if provided by API)
  foodTypes?: Array<{ id: string; code: string; name: string }>; // API payload (optional)
  currencyId?: string;
  currency?: { id: string; code: string; name: string; symbol?: string | null; decimalPlaces?: number | null };
  isOpen?: boolean;
  hasOffers?: boolean;
  amenities?: string[]; // 'wifi', 'parking', 'ac', etc.
  publicMenuEnabled?: boolean;
  public_menu_enabled?: boolean;
  publicMenuKey?: string;
  public_menu_key?: string;
}

export interface Category {
  id: string;
  restaurantId: string;
  name: string;
  image?: string;
  order: number;
  isActive: boolean;
  foodCategoryId?: string;
}

export interface MenuItem {
  id: string;
  restaurantId: string;
  categoryId: string;
  name: string;
  description: string;
  price: number;
  discountPrice?: number | null;
  discountPercent?: number | null;
  image?: string;
  tags: string[];
  spicyLevel?: number;
  isActive: boolean;
  preparationTime: number;
  servingSize?: string | null;
  inStock: boolean;
  variants?: MenuItemVariant[];
  addOns?: MenuItemAddOn[];
  customizationGroups?: MenuItemCustomizationGroup[];
}

export interface MenuItemVariant {
  id: string;
  name: string;
  price: number;
  isDefault?: boolean;
}

export interface MenuItemAddOn {
  id: string;
  name: string;
  price: number;
  category: string;
  source?: 'addon' | 'customization';
  customizationOptionId?: string;
  groupName?: string;
}

export interface MenuItemCustomizationOption {
  id: string;
  name: string;
  price: number;
  isDefault?: boolean;
  sortOrder?: number;
  category?: string;
}

export interface MenuItemCustomizationGroup {
  id: string;
  name: string;
  displayName: string;
  inputType: 'single' | 'multi';
  isRequired: boolean;
  minSelect: number;
  maxSelect?: number | null;
  sortOrder: number;
  options: MenuItemCustomizationOption[];
}

export interface Coupon {
  id: string;
  restaurantId: string;
  code: string;
  description: string;
  discountType: 'percentage' | 'flat';
  discountValue: number;
  minOrderValue: number;
  maxDiscount?: number;
  isActive: boolean;
  validFrom: string;
  validUntil: string;
  usageLimit?: number;
  usedCount: number;
  calculatedDiscount?: number;
  applicableMode?: 'all' | 'categories' | 'items';
  selectedCategoryIds?: string[];
  selectedItemIds?: string[];
}

export interface Order {
  id: string;
  restaurantId: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  items: OrderItem[];
  subtotal: number;
  deliveryFee: number;
  packagingCharges: number;
  tax: number;
  discount: number;
  couponCode?: string;
  total: number;
  status: OrderStatus;
  paymentMethod: 'cash' | 'card' | 'upi' | 'wallet';
  paymentStatus: 'pending' | 'paid' | 'failed';
  createdAt: string;
  updatedAt: string;
  estimatedDelivery?: string;
  assignedTo?: string;
  notes?: string;
  rating?: number;
}

export interface OrderItem {
  menuItemId: string;
  name: string;
  price: number;
  quantity: number;
  specialInstructions?: string;
  selectedVariant?: MenuItemVariant;
  selectedAddOns?: MenuItemAddOn[];
}

export type OrderStatus = 
  | 'pending' 
  | 'confirmed' 
  | 'preparing' 
  | 'ready' 
  | 'out_for_delivery' 
  | 'delivered' 
  | 'cancelled';

const LOGIN_PATHS = new Set([
  '/sign-in',
  '/customer-sign-in',
  '/customer-sign-up',
  '/delivery-partner-sign-in',
  '/delivery-boy-apply',
]);
const AUTH_PATHS = new Set([
  '/',
  '/sign-in',
  '/customer-sign-in',
  '/customer-sign-up',
  '/delivery-partner-sign-in',
  '/delivery-boy-apply',
  '/customer',
  '/customer/dine-out',
]);

function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [routePath, setRoutePath] = useState(() => window.location.pathname);
  const [deliveryRouteResolved, setDeliveryRouteResolved] = useState(false);
  const [deliveryApplicationApproved, setDeliveryApplicationApproved] = useState(false);


  const normalizeUiRole = (rawRole: unknown): UserRole => {
    const normalized = String(rawRole || '')
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_');

    if (normalized === 'SUPER_ADMIN' || normalized === 'SUPERADMIN') return 'superadmin';
    if (normalized === 'RESTAURANT_ADMIN' || normalized === 'RESTAURANTADMIN') return 'restaurant_admin';
    if (normalized === 'MANAGER') return 'manager';
    if (normalized === 'KITCHEN_STAFF' || normalized === 'KITCHENSTAFF' || normalized === 'STAFF') return 'staff';
    if (
      normalized === 'DELIVERY_PARTNER' ||
      normalized === 'DELIVERY_PATNER' ||
      normalized === 'DELIVERYPARTNER'
    ) {
      return 'delivery_partner';
    }
    return 'customer';
  };

  const normalizeUser = (rawUser: any): User => {
    const restaurants = getRestaurants();
    const fallbackRestaurantId = restaurants[0]?.id;
    const role = normalizeUiRole(rawUser?.role);
    const needsRestaurantContext = ['restaurant_admin', 'manager', 'staff'].includes(role);
    const resolvedRestaurantId =
      rawUser?.restaurantId ||
      rawUser?.restaurant_id ||
      rawUser?.restaurant?.id ||
      (needsRestaurantContext ? fallbackRestaurantId : undefined);

    return {
      ...rawUser,
      role,
      restaurantId: resolvedRestaurantId,
    };
  };

  useEffect(() => {
    const handlePopState = () => setRoutePath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);


  const navigateTo = (path: string, replace = false) => {
    if (window.location.pathname === path) {
      setRoutePath(path);
      return;
    }

    if (replace) {
      window.history.replaceState({}, '', path);
    } else {
      window.history.pushState({}, '', path);
    }

    setRoutePath(path);
  };

  useEffect(() => {
    // Initialize mock data
    initializeMockData();
    initializeAnalyticsData();
    
    // Check if user is logged in
    const savedUser = localStorage.getItem('currentUser');
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (savedUser && token) {
      const normalizedUser = normalizeUser(JSON.parse(savedUser));
      setCurrentUser(normalizedUser);
      localStorage.setItem('currentUser', JSON.stringify(normalizedUser));
      if (
        normalizedUser.role === 'delivery_partner' &&
        ['/delivery-partner-sign-in', '/delivery-boy-apply', '/sign-in'].includes(window.location.pathname)
      ) {
        navigateTo('/delivery-partner-application', true);
      }
    } else if (savedUser && !token) {
      localStorage.removeItem('currentUser');
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const resolveDeliveryRoute = async () => {
      if (!currentUser || currentUser.role !== 'delivery_partner') {
        setDeliveryRouteResolved(true);
        setDeliveryApplicationApproved(false);
        return;
      }

      setDeliveryRouteResolved(false);
      try {
        const response = await api.deliveryApplications.getMine();
        const application = response?.data || null;
        const isApproved = application?.status === 'APPROVED';
        setDeliveryApplicationApproved(isApproved);
        navigateTo(isApproved ? '/delivery-partner-dashboard' : '/delivery-partner-application', true);
      } catch {
        setDeliveryApplicationApproved(false);
        navigateTo('/delivery-partner-application', true);
      } finally {
        setDeliveryRouteResolved(true);
      }
    };

    void resolveDeliveryRoute();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'delivery_partner' || !deliveryRouteResolved) {
      return;
    }

    const allowedPaths = deliveryApplicationApproved
      ? ['/delivery-partner-dashboard', '/delivery-partner-application']
      : ['/delivery-partner-application'];

    const targetPath = deliveryApplicationApproved ? '/delivery-partner-dashboard' : '/delivery-partner-application';

    if (!allowedPaths.includes(routePath)) {
      navigateTo(targetPath, true);
    }
  }, [currentUser, deliveryRouteResolved, deliveryApplicationApproved, routePath]);

  const handleLogin = (user: User) => {
    const normalizedUser = normalizeUser(user);
    migrateGuestCart(normalizedUser.id);
    setCurrentUser(normalizedUser);
    localStorage.setItem('currentUser', JSON.stringify(normalizedUser));
    if (normalizedUser.role === 'delivery_partner') {
      navigateTo('/delivery-partner-application', true);
      return;
    }

    const redirectPath = sessionStorage.getItem('post_login_redirect');
    if (redirectPath && redirectPath.startsWith('/') && !LOGIN_PATHS.has(redirectPath)) {
      sessionStorage.removeItem('post_login_redirect');
      navigateTo(redirectPath, true);
    }
  };

  function migrateGuestCart(userId: string) {
    const guestKey = 'customer_cart_v1_guest';
    const targetKey = `customer_cart_v1_${userId}`;
    try {
      const guestCart = localStorage.getItem(guestKey);
      if (!guestCart) return;
      const existing = localStorage.getItem(targetKey);
      if (!existing) {
        localStorage.setItem(targetKey, guestCart);
      }
      localStorage.removeItem(guestKey);
    } catch {
      // ignore storage errors
    }
  }

  const handleLogout = () => {
    const previousRole = currentUser?.role;
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setDeliveryRouteResolved(false);
    setDeliveryApplicationApproved(false);

    if (previousRole === 'customer') {
      navigateTo('/', true);
      return;
    }

    if (previousRole === 'delivery_partner') {
      navigateTo('/delivery-partner-sign-in', true);
      return;
    }

    navigateTo('/sign-in', true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    const isPublicCustomerRoute =
      routePath === '/customer' ||
      routePath === '/customer/dine-out' ||
      routePath.startsWith('/r/') ||
      /^\/[a-f0-9]{16}\/?$/i.test(routePath) ||
      /^\/[a-z0-9._-]+\/[a-f0-9]{16}\/?$/i.test(routePath);

    if (LOGIN_PATHS.has(routePath)) {
      return <LoginPage onLogin={handleLogin} />;
    }

    if (routePath === '/') {
      return (
        <LandingPage
          onSignInClick={() => navigateTo('/customer-sign-in')}
          onSignupClick={() => navigateTo('/customer-sign-up')}
          onBrowseClick={() => navigateTo('/customer')}
          onDineOutClick={() => navigateTo('/customer/dine-out')}
        />
      );
    }

    if (isPublicCustomerRoute) {
      const guestUser: User = { id: 'guest', email: '', name: 'Guest', role: 'customer' };
      return (
        <ThemeProvider>
          <CustomerView user={guestUser} onLogout={() => navigateTo('/', true)} />
        </ThemeProvider>
      );
    }

    if (!AUTH_PATHS.has(routePath)) {
      navigateTo('/', true);
      return null;
    }

    return (
      <LandingPage
        onSignInClick={() => navigateTo('/customer-sign-in')}
        onSignupClick={() => navigateTo('/customer-sign-up')}
        onBrowseClick={() => navigateTo('/customer')}
        onDineOutClick={() => navigateTo('/customer/dine-out')}
      />
    );
  }

  if (currentUser.role === 'delivery_partner' && !deliveryRouteResolved) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading delivery partner workspace...</p>
        </div>
      </div>
    );
  }

  // Route to appropriate dashboard based on role
  switch (currentUser.role) {
    case 'superadmin':
      return <SuperAdminDashboard user={currentUser} onLogout={handleLogout} />;
    case 'restaurant_admin':
      return <RestaurantAdminDashboard user={currentUser} onLogout={handleLogout} />;
    case 'manager':
      return <ManagerDashboard user={currentUser} onLogout={handleLogout} />;
    case 'staff':
      return <StaffDashboard user={currentUser} onLogout={handleLogout} />;
    case 'delivery_partner':
      if (deliveryApplicationApproved && routePath === '/delivery-partner-dashboard') {
        return (
          <DeliveryPartnerDashboard
            user={currentUser}
            onLogout={handleLogout}
            onOpenApplication={() => navigateTo('/delivery-partner-application')}
          />
        );
      }
      return (
        <DeliveryPartnerApplicationPage
          user={currentUser}
          onLogout={handleLogout}
          onOpenDashboard={() => {
            if (!deliveryApplicationApproved) return;
            navigateTo('/delivery-partner-dashboard');
          }}
        />
      );
    case 'customer':
      if (routePath === '/') {
        return (
          <LandingPage
            onSignInClick={() => navigateTo('/customer-sign-in')}
            onSignupClick={() => navigateTo('/customer-sign-up')}
            onBrowseClick={() => navigateTo('/customer')}
            onDineOutClick={() => navigateTo('/customer/dine-out')}
          />
        );
      }
      return (
        <ThemeProvider>
          <CustomerView user={currentUser} onLogout={handleLogout} />
        </ThemeProvider>
      );
    default:
      return <LoginPage onLogin={handleLogin} />;
  }
}

export default App;
